/**
 * BreakPlaylistChips - Atajos 1-tap para playlists Spotify "salvavidas"
 * (cocktail, cena, pausa DJ, prefiesta, cierre).
 *
 * Lee/escribe en `db.users.{id}.spotify_break_playlists` vía
 * GET/PUT /api/spotify/break-playlists.
 * Para lanzar usa POST /api/spotify/play con context_uri (existente extendido).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Play, Pause, Settings, Wine, Utensils, Coffee, PartyPopper, Moon, Save, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config';

const SLOT_META = {
  cocktail:   { label: 'Cocktail',   Icon: Wine,         color: 'bg-amber-700 hover:bg-amber-600' },
  cena:       { label: 'Cena',       Icon: Utensils,     color: 'bg-rose-700 hover:bg-rose-600' },
  pausa:      { label: 'Pausa DJ',   Icon: Coffee,       color: 'bg-slate-700 hover:bg-slate-600' },
  prefiesta:  { label: 'Pre-fiesta', Icon: PartyPopper,  color: 'bg-fuchsia-700 hover:bg-fuchsia-600' },
  cierre:     { label: 'Cierre',     Icon: Moon,         color: 'bg-indigo-700 hover:bg-indigo-600' },
};

const BreakPlaylistChips = () => {
  const [slots, setSlots] = useState(['cocktail', 'cena', 'pausa', 'prefiesta', 'cierre']);
  const [playlists, setPlaylists] = useState({});
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [launching, setLaunching] = useState(null);
  // Estado de reproducción local para toggle play/pause en el mismo chip
  const [activeSlot, setActiveSlot] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/spotify/break-playlists`, { headers: headers() });
      setSlots(res.data.slots || []);
      setPlaylists(res.data.playlists || {});
      setCanEdit(!!res.data.can_edit);
    } catch {
      // silencioso: si falla, los chips muestran "configurar"
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: load
  useEffect(() => { load(); }, []);

  // Sincroniza el estado play/pause si el DJ controla desde Spotify directamente
  useEffect(() => {
    if (!activeSlot) return;
    const token = window.spotifyAccessToken;
    if (!token) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/spotify/playback-state`, {
          params: { access_token: token },
        });
        if (cancelled) return;
        const playing = !!r.data?.is_playing;
        setIsPaused(!playing);
      } catch {
        // silencioso
      }
    };
    tick();
    const id = setInterval(tick, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, [activeSlot]);

  const handleChipTap = async (slot) => {
    const token = window.spotifyAccessToken;
    if (!token) {
      toast.error('Conecta Spotify primero (Ajustes → Spotify)');
      return;
    }
    // Toggle play/pause si es el chip activo
    if (slot === activeSlot) {
      setLaunching(slot);
      try {
        if (isPaused) {
          await axios.post(`${BACKEND_URL}/api/spotify/playback/resume`, { access_token: token });
          setIsPaused(false);
          toast.success('▶️ Reanudado');
        } else {
          await axios.post(`${BACKEND_URL}/api/spotify/playback/pause`, { access_token: token });
          setIsPaused(true);
          toast.success('⏸ Pausado');
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Error controlando reproducción', { duration: 6000 });
      } finally {
        setLaunching(null);
      }
      return;
    }
    // Lanzar playlist nueva
    const entry = playlists[slot];
    if (!entry?.playlist_uri) {
      if (canEdit) {
        toast.info(`Configura primero la playlist de "${SLOT_META[slot]?.label || slot}"`);
        setEditorOpen(true);
      } else {
        toast.info('Esta playlist salvavidas aún no la ha configurado el administrador');
      }
      return;
    }
    setLaunching(slot);
    try {
      // 1) Buscar dispositivos disponibles (aunque estén inactivos).
      const devRes = await axios.get(
        `${BACKEND_URL}/api/spotify/devices`,
        { params: { access_token: token } }
      );
      const devices = devRes.data?.devices || [];
      if (devices.length === 0) {
        toast.error(
          'Abre Spotify en tu móvil, PC o web y mantenlo abierto. Luego reintenta.',
          { duration: 6000 }
        );
        return;
      }
      const target = devices.find((d) => d.is_active) || devices[0];
      // 2) Lanzar la playlist en ese dispositivo.
      await axios.post(`${BACKEND_URL}/api/spotify/play`, {
        access_token: token,
        context_uri: entry.playlist_uri,
        device_id: target.id,
      });
      setActiveSlot(slot);
      setIsPaused(false);
      toast.success(`▶️ ${entry.label || slot} sonando en ${target.name}`);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Error lanzando playlist';
      toast.error(detail, { duration: 6000 });
    } finally {
      setLaunching(null);
    }
  };

  const openEditor = () => {
    setDraft(JSON.parse(JSON.stringify(playlists)));
    setEditorOpen(true);
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${BACKEND_URL}/api/spotify/break-playlists`,
        { playlists: draft },
        { headers: headers() }
      );
      setPlaylists(res.data.playlists || {});
      setEditorOpen(false);
      toast.success('Playlists guardadas');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error guardando');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="break-playlists-chips">
      {slots.map((slot) => {
        const meta = SLOT_META[slot] || { label: slot, Icon: Play, color: 'bg-slate-700 hover:bg-slate-600' };
        const entry = playlists[slot];
        const configured = !!entry?.playlist_uri;
        const isActive = slot === activeSlot;
        const showPauseIcon = isActive && !isPaused;
        const Icon = showPauseIcon ? Pause : (isActive ? Play : meta.Icon);
        return (
          <button
            key={slot}
            type="button"
            onClick={() => handleChipTap(slot)}
            disabled={launching === slot}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-white transition-all ${
              configured ? meta.color : 'bg-slate-800 border border-dashed border-slate-600 text-slate-400'
            } ${launching === slot ? 'opacity-60' : ''} ${
              isActive && !isPaused ? 'ring-2 ring-emerald-400 animate-pulse shadow-lg shadow-emerald-500/30' : ''
            } ${
              isActive && isPaused ? 'ring-2 ring-emerald-600/50' : ''
            }`}
            title={
              isActive
                ? (isPaused ? `Reanudar ${entry.label || meta.label}` : `Pausar ${entry.label || meta.label}`)
                : (configured ? `Lanzar: ${entry.label}` : 'Sin configurar — toca para añadir')
            }
            data-testid={`break-chip-${slot}`}
          >
            <Icon className="w-3 h-3" />
            <span>{entry?.label || meta.label}</span>
          </button>
        );
      })}
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className={`h-7 px-2 text-slate-400 hover:text-white ${canEdit ? '' : 'hidden'}`}
        onClick={openEditor}
        title="Configurar playlists salvavidas"
        data-testid="break-playlists-config-btn"
      >
        <Settings className="w-3.5 h-3.5" />
      </Button>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Playlists salvavidas Spotify</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-400 -mt-2">
            Pega el link o URI de cada playlist. Tap en el chip = lanza esa playlist en tu Spotify Connect activo.
          </p>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {slots.map((slot) => {
              const meta = SLOT_META[slot] || { label: slot, Icon: Play };
              const Icon = meta.Icon;
              const entry = draft[slot] || { label: meta.label, playlist_uri: '' };
              return (
                <div key={slot} className="space-y-1">
                  <div className="flex items-center gap-2 text-slate-200 text-sm">
                    <Icon className="w-4 h-4" />
                    <span className="font-medium">{meta.label}</span>
                    {entry.playlist_uri && (
                      <button
                        type="button"
                        onClick={() => {
                          const next = { ...draft };
                          delete next[slot];
                          setDraft(next);
                        }}
                        className="ml-auto text-red-400 hover:text-red-300 text-xs flex items-center gap-1"
                      >
                        <X className="w-3 h-3" /> quitar
                      </button>
                    )}
                  </div>
                  <Input
                    value={entry.playlist_uri || ''}
                    onChange={(e) => setDraft({ ...draft, [slot]: { ...entry, playlist_uri: e.target.value } })}
                    placeholder="https://open.spotify.com/playlist/… o spotify:playlist:…"
                    className="bg-slate-800 border-slate-600 text-white text-sm h-8"
                    data-testid={`break-input-${slot}`}
                  />
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)} disabled={saving} className="border-slate-600">
              Cancelar
            </Button>
            <Button onClick={saveDraft} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="break-save-btn">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BreakPlaylistChips;
